import { onMounted, onBeforeUnmount } from 'vue';

// Keyboard binding stack — port of public/js/components/KeyboardManager.js.
//
// Each page or modal pushes a layer of bindings on mount and pops it on
// unmount. Dispatch walks the stack top→bottom; the first matching binding
// fires (and may call `stopPropagation()` to halt). Modals therefore
// "shadow" page-level bindings while open without mutual coordination.
//
// Binding shape:
//   { key: 'ArrowDown', ctrl?: bool, shift?: bool, alt?: bool, meta?: bool,
//     when?: () => bool, handler: (ev) => void, label?: string }

export interface KeyBinding {
    key: string;
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
    when?: () => boolean;
    handler: (ev: KeyboardEvent) => void;
    /** Human-readable description for the help overlay. */
    label?: string;
}

interface Layer {
    id: number;
    /**
     * Human-readable context this layer belongs to ("Live TV", "Player",
     * "Dialog"). Only used to group rows in <KeyboardShortcuts>; dispatch
     * never reads it. Layers without one are grouped under "Other".
     */
     scope?: string;
    bindings: KeyBinding[];
}

const stack: Layer[] = [];
let nextId = 1;
let installedListener = false;

function matches(b: KeyBinding, ev: KeyboardEvent): boolean {
    if (b.key.toLowerCase() !== ev.key.toLowerCase()) return false;
    if (!!b.ctrl !== ev.ctrlKey) return false;
    if (!!b.shift !== ev.shiftKey) return false;
    if (!!b.alt !== ev.altKey) return false;
    if (!!b.meta !== ev.metaKey) return false;
    if (b.when && !b.when()) return false;
    return true;
}

function ensureGlobalListener() {
    if (installedListener) return;
    installedListener = true;
    window.addEventListener('keydown', dispatchKeyBindings);
}

/** Allow a media host to dispatch channel keys before native player shortcuts consume them. */
export function dispatchKeyBindings(ev: KeyboardEvent): void {
    // Don't intercept while user is typing in an input/textarea/contenteditable.
    const target = ev.target as HTMLElement | null;
    if (
        target &&
        (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable)
    ) {
        // Allow page-defined bindings only when explicitly typed for this case.
        // Most bindings (single-key shortcuts) are noisy if they fire while typing.
        return;
    }
    for (let i = stack.length - 1; i >= 0; i--) {
        const layer = stack[i];
        if (!layer) continue;
        for (const b of layer.bindings) {
            if (matches(b, ev)) {
                b.handler(ev);
                if (ev.defaultPrevented || ev.cancelBubble) return;
            }
        }
    }
}

export function useKeyBindings(bindings: KeyBinding[], opts: { scope?: string } = {}) {
    ensureGlobalListener();
    let id: number | null = null;

    onMounted(() => {
        id = nextId++;
        stack.push({ id, bindings, ...(opts.scope ? { scope: opts.scope } : {}) });
    });

    onBeforeUnmount(() => {
        if (id === null) return;
        const idx = stack.findIndex((l) => l.id === id);
        if (idx !== -1) stack.splice(idx, 1);
        id = null;
    });
}

/** A binding as the help overlay sees it: the layer it came from, plus the binding. */
export interface ActiveBinding extends KeyBinding {
    scope: string;
}

/**
 * The bindings a keypress would actually reach right now, most-specific first.
 *
 * This deliberately mirrors `matches()` rather than dumping the stack, because
 * the overlay's whole job is to answer "what does this key do HERE" and three
 * things make a raw dump wrong:
 *
 *   - **Order.** Dispatch walks the stack top-down, so the newest layer wins.
 *     A bottom-up `flatMap` (what this used to return, unused) lists the
 *     shadowed binding first.
 *   - **`when()`.** A binding whose predicate is false does nothing when
 *     pressed — listing it promises a shortcut that will not fire. Live's
 *     arrow keys are exactly this: gated on a per-user preference.
 *   - **Shadowing.** Two layers can bind the same key; only the top one runs.
 *     Dedupe therefore keys on the FULL identity `matches()` compares —
 *     key plus all four modifiers — not on `key` alone, or `Esc` in a dialog
 *     would hide `Esc` on the page beneath it while `Ctrl+K` and `K` would
 *     collapse into one row.
 */
export function listActiveBindings(): ActiveBinding[] {
    const seen = new Set<string>();
    const out: ActiveBinding[] = [];
    for (let i = stack.length - 1; i >= 0; i--) {
        const layer = stack[i];
        if (!layer) continue;
        for (const b of layer.bindings) {
            if (b.when && !b.when()) continue;
            const identity = [b.key.toLowerCase(), !!b.ctrl, !!b.shift, !!b.alt, !!b.meta].join('|');
            if (seen.has(identity)) continue;
            seen.add(identity);
            out.push({ ...b, scope: layer.scope ?? 'Other' });
        }
    }
    return out;
}
